import { useEffect, useState } from "react";
import ColumnEditor from "./ColumnEditor";
import ColumnTree from "./ColumnTree";
import PDFPreview from "./PDFPreview";
import TablePreview from "./TablePreview";
import TemplateMetadata from "./TemplateMetadata";
import TemplatePicker from "./TemplatePicker";
import { useTableTemplateStore } from "../../store/tableTemplateStore";
import { useLocation, useNavigate } from "react-router-dom";
import { validateColumnWidthPercentages } from "./tableBuilderUtils/columnUtils";
import "./tableBuilder.css";

export default function TableTemplateBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const schema = useTableTemplateStore((s) => s.editorSchema);
  const selectedTemplate = useTableTemplateStore((s) => s.selectedTemplate);
  const lockState = useTableTemplateStore((s) => s.lockState);
  const createTemplate = useTableTemplateStore((s) => s.createTemplate);
  const saveTemplate = useTableTemplateStore((s) => s.saveTemplate);
  const clearSelection = useTableTemplateStore((s) => s.clearSelection);
  const isSaving = useTableTemplateStore((s) => s.isSaving);
  const error = useTableTemplateStore((s) => s.error);
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    setValidationError(null);
  }, [schema]);

  if (!schema) {
    return (
      <div className="template-start">
        <TemplatePicker
          mode="builder"
          onClose={returnTo ? () => navigate(returnTo) : undefined}
          returnTo={returnTo}
        />
      </div>
    );
  }

  const isLocked = !!lockState?.isLocked;

  const handleSave = async () => {
    if (isLocked) return;
    if (!schema) return;

    const validation = validateColumnWidthPercentages(schema.columns);
    if (!validation.isValid) {
      setValidationError(validation.errors[0] || "Column width validation failed.");
      return;
    }

    setValidationError(null);

    if (selectedTemplate) {
      await saveTemplate();
      return;
    }

    const templateName = schema.templateName?.trim() || "new_table_template";
    const displayTitle = schema.displayTitle?.trim() || "New Table Template";
    await createTemplate(templateName, displayTitle, schema);
  };

  const pageTitle =
    schema.displayTitle?.trim() ||
    schema.templateName?.trim() ||
    selectedTemplate?.displayTitle ||
    selectedTemplate?.templateName ||
    "Table Template Builder";

  return (
    <div className="builder-container">
      <div className="builder-header">
        <div className="builder-title-wrap">
          <h2 className="builder-title">{pageTitle}</h2>
          <div className="builder-subtitle">
            Nested column editor with live preview
            {isLocked ? " | Locked" : ""}
          </div>
        </div>

        <div className="builder-actions">
          {returnTo ? (
            <button
              className="btn btn-sm btn-outline-secondary"
              type="button"
              onClick={() => navigate(returnTo)}
              disabled={isSaving}
            >
              Back to Authoring
            </button>
          ) : null}
          <button className="btn btn-sm btn-primary" type="button" onClick={() => void handleSave()} disabled={isLocked || isSaving}>
            {isSaving ? "Saving..." : "Save Template"}
          </button>
          <button className="btn btn-sm btn-primary" type="button" onClick={clearSelection} disabled={isSaving}>
            Change Template
          </button>
        </div>
      </div>

      {validationError ? <div className="builder-error">{validationError}</div> : null}
      {error ? <div className="builder-error">{error}</div> : null}

      <div className="builder-layout row g-0">
        <aside className="tree-panel col-12 col-md-3">
          <ColumnTree />
          <TemplateMetadata />
        </aside>

        <section className="editor-panel col-12 col-md-3">          
          <ColumnEditor />
        </section>

        <aside className="preview-panel col-12 col-md-6">
          <TablePreview />
          <PDFPreview />
        </aside>
      </div>
    </div>
  );
}
